import React, { useEffect, useMemo, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import { Button, Stack } from '@sureco/design-system'
import {
  downloadFile,
  getTerminationFiles,
} from '../api/terminations-enrollment-platform-api-client'
import { DEFAULT_FILES_PAGINATION_LIMIT } from '../constants'
import { TerminationFileSummaryDto } from '../api/dto/termination-summary.dto'
import { withRouter, browserHistory } from 'react-router'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { TerminationStatusEnum } from '../api/enums/terminations-status.enum'
import { Typography } from 'antd'
import { filterClients } from '../CensusReview/utils/filter-clients'
import { CensusClientFilter } from '../types/census-client-filter'
import { TerminationFilesRequestDto } from '../api/dto/termination-files-request.dto'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'

const { Text } = Typography

const ProcessingTerminationsGrid = ({
  clientNameLookup,
  refreshKey,
  filter,
}: {
  clientNameLookup: Record<string, string>
  refreshKey: number
  filter?: CensusClientFilter
}) => {
  const [grid, setGrid] = useState<GridReadyEvent>()
  const { clients } = useCensusV2Clients()
  const role = useSelector(authSelectors.getRole) as string
  const clientIds = filterClients(clients, filter, role).map(
    (client) => client.id
  )

  const clickView = (publicKey: string) => {
    browserHistory.push(`/v2-census-management/bulk-terminations/${publicKey}`)
  }

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'File Name',
        field: 'fileName',
        flex: 1,
        cellRenderer: (params) => {
          const { fileName, publicKey } = params.data

          return (
            <span
              onClick={() => downloadFile(publicKey, fileName)}
              style={{
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {fileName}
            </span>
          )
        },
      },
      {
        headerName: 'Customer',
        field: 'clientPublicKey',
        flex: 1,
        sortable: false,
        valueGetter: (params) => params.data.clientPublicKey,
        cellRenderer: (params) => {
          const clientName = clientNameLookup[params.value]
          return <Text>{clientName || 'loading...'}</Text>
        },
      },
      {
        headerName: 'Uploaded At',
        field: 'uploadedAt',
        flex: 1,
        valueFormatter: ({ value }) => new Date(value).toLocaleString(),
      },
      { headerName: 'Records', field: 'rowCount', flex: 1 },
      {
        headerName: '',
        field: 'View',
        cellRenderer: (params) => {
          return (
            <Button
              onClick={() => clickView(params.data.publicKey)}
              style={{ marginLeft: 'auto' }}
            >
              View
            </Button>
          )
        },
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        width: 120,
      },
    ],
    [clientNameLookup]
  )

  const datasource = useMemo(() => {
    if (clients.length === 0) return undefined

    return {
      getRows: async (
        params: IServerSideGetRowsParams<TerminationFileSummaryDto>
      ) => {
        const limit =
          (params.request.endRow ?? DEFAULT_FILES_PAGINATION_LIMIT) -
          (params.request.startRow ?? 0)
        try {
          const page = Math.floor((params.request.startRow ?? 1) / limit) + 1
          const status = TerminationStatusEnum.IN_PROCESS
          const queryParams: TerminationFilesRequestDto = {
            page,
            limit,
            status,
          }

          if (filter?.selectedClient?.id) {
            queryParams.clientPublicKey = [filter.selectedClient.id]
          } else {
            queryParams.clientPublicKey = clientIds
          }

          const result = await getTerminationFiles(queryParams)
          const completedFiles = result.data ?? []

          params.success({
            rowData: completedFiles,
            rowCount: result.meta.total,
          })
        } catch (error) {
          console.error('❌ Failed to fetch files', error)
          params.fail()
        }
      },
    }
  }, [clients, filter])

  useEffect(() => {
    if (grid && datasource) {
      grid.api.setServerSideDatasource(datasource)
    }
  }, [grid, datasource, refreshKey])

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        Processing
      </Stack>
      {datasource && (
        <AgGridReact
          rowStyle={{ backgroundColor: 'white', marginBottom: '20px' }}
          className="censusReviewGrid ag-theme-alpine"
          getRowId={({ data }) => data.publicKey}
          pagination
          paginationPageSize={DEFAULT_FILES_PAGINATION_LIMIT}
          cacheBlockSize={DEFAULT_FILES_PAGINATION_LIMIT}
          columnDefs={columnDefs}
          rowModelType="serverSide"
          onGridReady={(params) => {
            setGrid(params)
          }}
          domLayout="autoHeight"
          suppressHorizontalScroll
          paginationPageSizeSelector={[10, 20, 50, 100]}
        />
      )}
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default withRouter(ProcessingTerminationsGrid)
